import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PanelDirective } from '../../directives/panel.directive';
import RestService from '../../services/core-rest.service';
import Session from '../../utils/session.service';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { ItemNoContentComponent } from '../../components/item-no-content/item-no-content.component';
import DialogServices from '../../services/dialog.service';
import {TranslatePipe} from "../../filters/translate.pipe";
import AngularFilterHelperService from "../../services/angular-filter-helper.service";
import { Router } from '@angular/router';
import ToolkitService from '../../services/toolkit.service';

@Component({
    standalone: true,
    selector: 'my-training',
    templateUrl: './my-training.component.html',
    imports: [CommonModule, PanelDirective, LoaderOverlayComponent, ItemNoContentComponent, TranslatePipe]
})
export default class MyTrainingComponent implements OnInit {
    private restService: RestService = inject(RestService);
    private session: Session = inject(Session);
    private dialogServices: DialogServices = inject(DialogServices);
    private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
    private router: Router = inject(Router);
	private toolkitService: ToolkitService = inject(ToolkitService);


    vm: any = undefined;

    constructor() {
        this.vm = this;
    }

    ngOnInit() {
        this.vm.loading = true;
        this.vm.error = false;
        this.vm.circumference = 2 * Math.PI * 34;
        this.vm.progressOffset = this.vm.circumference;
        this.vm.user = this.session.getSession().user;
        this.vm.origin = this.vm.user.origin;
        this.vm.isSiffiUser = this.vm.user && this.vm.user.origin === 'siffi.com' && !this.vm.user.businessUser;
        this.vm.isBusinessUser = this.vm.user.businessUserType === 'BUSINESS_CLIENT' || this.vm.user.businessUserType === 'SERVICE_PROVIDER' || this.vm.user.businessUserType === 'SERVICE_OWNER';
        if (!this.vm.isSiffiUser) {
            this.vm.loading = false;
            return;
        }
        if (!this.vm.isBusinessUser) {
            this.loadTraining();
            this.loadAssignedToolkitSummary();
        }
        this.vm.primaryActionLabel = this.primaryActionLabel;
        this.vm.onPrimaryAction = this.onPrimaryAction;
        this.vm.getTrainingEnrollmentMessage = this.getTrainingEnrollmentMessage;
        this.vm.previousBannerProgram = this.previousBannerProgram;
        this.vm.nextBannerProgram = this.nextBannerProgram;
        this.vm.selectBannerProgram = this.selectBannerProgram;
        this.vm.getProgramAriaLabel = this.getProgramAriaLabel;
		this.vm.openMyToolkit = this.openMyToolkit.bind(this);
    }

	openMyToolkit(event?) {
		if (event?.preventDefault) event.preventDefault();
		if (event?.stopPropagation) event.stopPropagation();
		this.router.navigate(['/my-exercises']);
	}

    loadAssignedToolkitSummary() {
		Promise.all([
			this.toolkitService.getCatalogue({}).catch(() => null),
			this.toolkitService.getMyAssignmentsList({}).catch(() => []),
			this.toolkitService.getMyAssessmentAssignmentsList({}).catch(() => [])
		]).then(([catalogue, ex, sv]) => {
			const resources: any[] = Array.isArray(catalogue?.resources) ? catalogue.resources : [];
			const assessments: any[] = Array.isArray(catalogue?.assessments) ? catalogue.assessments : [];

			const exercises = (Array.isArray(ex) ? ex : []).filter((x) => x && !x.isDeleted);
			const surveys = (Array.isArray(sv) ? sv : []).filter((x) => x && !x.isDeleted);
			this.vm.assignedToolkitCount = exercises.length + surveys.length;
			this.vm.assignedToolkit = this.vm.assignedToolkitCount > 0;

			const toTime = (x: any): number => {
				const raw = x?.assignedAt || x?.created || x?.createdAt || x?.updatedAt || null;
				if (!raw) return 0;
				const t = new Date(raw).getTime();
				return isFinite(t) ? t : 0;
			};

			const latestExercise = exercises.slice().sort((a, b) => toTime(b) - toTime(a))[0] || null;
			const latestSurvey = surveys.slice().sort((a, b) => toTime(b) - toTime(a))[0] || null;

			const latest =
				(latestExercise && latestSurvey)
					? (toTime(latestExercise) >= toTime(latestSurvey) ? { kind: 'exercise', a: latestExercise } : { kind: 'survey', a: latestSurvey })
					: latestExercise
						? { kind: 'exercise', a: latestExercise }
						: latestSurvey
							? { kind: 'survey', a: latestSurvey }
							: null;

			this.vm.latestAssignedToolkitTitle = '';
			this.vm.latestAssignedToolkitSub = '';

			if (latest?.kind === 'exercise') {
				const rid = latest.a?.resourceId;
				const res = resources.find((r) => (r?.id ?? '').toString() === (rid ?? '').toString()) || null;
				this.vm.latestAssignedToolkitTitle = (res?.title || res?.name || '').toString();
				this.vm.latestAssignedToolkitSub = (res?.summary || res?.description || '').toString();
			} else if (latest?.kind === 'survey') {
				const aid = latest.a?.assessmentId;
				const asmt = assessments.find((r) => (r?.id ?? '').toString() === (aid ?? '').toString()) || null;
				this.vm.latestAssignedToolkitTitle = (asmt?.title || asmt?.name || '').toString();
				this.vm.latestAssignedToolkitSub = (asmt?.description || asmt?.summary || '').toString();
			}
		}).catch(() => {
			this.vm.assignedToolkitCount = 0;
			this.vm.assignedToolkit = false;
			this.vm.latestAssignedToolkitTitle = '';
			this.vm.latestAssignedToolkitSub = '';
		});
	}

    loadTraining() {
        this.restService.getTrainingLearnerSummary()
            .then((summary) => {
                this.vm.summary = summary || {};
                this.vm.completedModules = summary?.completedModules || 0;
                this.vm.totalModules = summary?.totalModules || 0;
                this.vm.certificateCode = summary?.certificateCode;
                return this.restService.getTrainingProgramsForLearner().catch(() => []);
            })
            .then(async (programs) => {
                const items = Array.isArray(programs) ? programs : [];
                const summaries = await Promise.all(items.map((program) => {
                    const params = program?.versionCode ? { programVersionCode: program.versionCode } : {};
                    return this.restService.getTrainingLearnerSummary(params)
                        .then((summary) => ({ program, summary }))
                        .catch(() => ({ program, summary: null }));
                }));
                this.vm.assignedPrograms = summaries
                    .filter((item) => {
                        const totalModules = item.summary?.totalModules ?? item.program?.totalModules ?? 0;
                        const completedModules = item.summary?.completedModules ?? 0;
                        return totalModules === 0 || completedModules < totalModules;
                    })
                    .map((item) => item.program);
                this.vm.assignedTraining = this.vm.assignedPrograms.length > 0;
                this.vm.bannerIndex = 0;
                if (!this.vm.assignedTraining) {
                    this.vm.bannerVisible = false;
                    this.vm.loading = false;
                    return null;
                }
                return this.updateBannerProgram(0);
            })
            .catch((error) => {
                console.log(error);
                this.vm.error = true;
                this.vm.loading = false;
            });
    }

    updateBannerProgram(index) {
        if (!this.vm.assignedPrograms?.length) {
            this.vm.bannerVisible = false;
            this.vm.loading = false;
            return Promise.resolve();
        }

        const normalizedIndex = ((index % this.vm.assignedPrograms.length) + this.vm.assignedPrograms.length) % this.vm.assignedPrograms.length;
        this.vm.bannerIndex = normalizedIndex;
        this.vm.program = this.vm.assignedPrograms[normalizedIndex];
        this.vm.totalModules = this.vm.program?.totalModules || this.vm.totalModules || 0;
        this.vm.bannerVisible = !!this.vm.program;
        this.computeProgress();

        const params = this.vm.program?.versionCode ? { programVersionCode: this.vm.program.versionCode } : {};
        return this.restService.getTrainingLearnerSummary(params)
            .then((summary) => {
                this.vm.summary = summary || {};
                this.vm.completedModules = summary?.completedModules || 0;
                this.vm.totalModules = summary?.totalModules || this.vm.program?.totalModules || 0;
                this.vm.certificateCode = summary?.certificateCode;
                this.computeProgress();
                return this.restService.getTrainingLearnerModules(params);
            })
            .then((modules) => {
                this.vm.modules = modules || [];
                this.pickCurrentModule(this.vm.modules);
                this.computeProgress();
                this.vm.loading = false;
            })
            .catch((error) => {
                console.log(error);
                this.vm.error = true;
                this.vm.loading = false;
            });
    }

    computeProgress() {
        const total = this.vm.totalModules || 0;
        const completed = this.vm.completedModules || 0;
        this.vm.allCompleted = total > 0 && completed >= total;
        const ratio = total > 0 ? Math.min(1, Math.max(0, completed / total)) : 0;
        this.vm.progressOffset = this.vm.circumference * (1 - ratio);
    }

    pickCurrentModule(modules) {
        if (!modules || !modules.length) return;
        const completedCount = modules.filter((m) => m.progress && m.progress.status === 'passed').length;
        if (!this.vm.completedModules) {
            this.vm.completedModules = completedCount;
        }
        if (!this.vm.totalModules) {
            this.vm.totalModules = modules.length;
        }
        const firstPending = modules.find((m) => !m.progress || m.progress.status !== 'passed');
        const current = firstPending || modules[modules.length - 1];
        this.vm.currentModuleTitle = current?.module?.title;
        this.vm.currentModuleId = current?.module?.id;
        this.vm.currentModuleKey = current?.module?.moduleKey;
    }

    primaryActionLabel() {
        if (this.vm.allCompleted) {
            return this.angularFilterHelperService.translate('Training.My_Training.View_Certificate');
        }
        const currentModuleNumber = this.vm.currentModuleKey?.split('-')[1];
        if (!currentModuleNumber) {
            return this.angularFilterHelperService.translate('Training.My_Training.Continue_Module');
        }
        return this.angularFilterHelperService.translate('Training.My_Training.Continue_Module') + ' ' + currentModuleNumber;
    }

    onPrimaryAction() {
        const queryParams = this.vm.program?.versionCode ? { programVersionCode: this.vm.program.versionCode } : undefined;
        if (this.vm.allCompleted) {
            this.router.navigate(['/my-training', 'certificate'], { queryParams });
            return;
        }
        if (this.vm.currentModuleId) {
            this.router.navigate(['/my-training', 'module', this.vm.currentModuleId], { queryParams });
        }
    }

    previousBannerProgram(event?) {
        if (event?.preventDefault) event.preventDefault();
        if (event?.stopPropagation) event.stopPropagation();
        this.vm.loading = true;
        this.updateBannerProgram((this.vm.bannerIndex || 0) - 1);
    }

    nextBannerProgram(event?) {
        if (event?.preventDefault) event.preventDefault();
        if (event?.stopPropagation) event.stopPropagation();
        this.vm.loading = true;
        this.updateBannerProgram((this.vm.bannerIndex || 0) + 1);
    }

    selectBannerProgram(index, event?) {
        if (event?.preventDefault) event.preventDefault();
        if (event?.stopPropagation) event.stopPropagation();
        if (index === this.vm.bannerIndex) return;
        this.vm.loading = true;
        this.updateBannerProgram(index);
    }

    getTrainingEnrollmentMessage() {
        const program = this.vm.program;

        if (!program) return '';
        let message = this.angularFilterHelperService.translate('Training.My_Training.Enrollment_Message');

        if (message.indexOf('%s') !== -1) {
            message = message.replace('%s', program.totalModules);
        }
        if (message.indexOf('%v') !== -1) {
            message = message.replace('%v', program.completionPeriod);
        }
        if (message.indexOf('%t') !== -1) {
            message = message.replace('%t', this.vm.currentModuleTitle);
        }

        return message;
    }

    getProgramAriaLabel(index) {
        return this.angularFilterHelperService.translate('Training.My_Training.Program_Aria').replace('%s', index + 1);
    }
}
